/**
 * Studio Settings — Notification preferences, Team availability, and a quick
 * link to per-channel approval-flow editor.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Settings, Bell, Calendar, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function StudioSettings() {
  const [pref, setPref] = useState({ whatsapp: true, email: true, in_app: true, slack: false });
  const [avail, setAvail] = useState({ working_hours: {}, weekly_off: [], leave_dates: [], holidays: [] });
  const [newLeave, setNewLeave] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, a] = await Promise.all([
          axios.get(`${API}/api/studio/notification-prefs`, { withCredentials: true }),
          axios.get(`${API}/api/studio/availability`, { withCredentials: true }),
        ]);
        setPref(p.data);
        setAvail({
          working_hours: a.data.working_hours || {},
          weekly_off: a.data.weekly_off || [],
          leave_dates: a.data.leave_dates || [],
          holidays: a.data.holidays || [],
        });
      } catch (_) {}
    })();
  }, []);

  const savePref = async (next) => {
    setPref(next);
    try {
      await axios.put(`${API}/api/studio/notification-prefs`, next, { withCredentials: true });
      toast.success("Preferences saved");
    } catch (err) {
      toast.error("Failed");
    }
  };

  const saveAvail = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/studio/availability`, avail, { withCredentials: true });
      toast.success("Availability saved");
    } catch (err) {
      toast.error("Failed");
    } finally { setSaving(false); }
  };

  const toggleOff = (n) => {
    setAvail((a) => ({
      ...a,
      weekly_off: a.weekly_off.includes(n) ? a.weekly_off.filter((x) => x !== n) : [...a.weekly_off, n],
    }));
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <Link to="/studio" className="studio-back" data-testid="settings-back">
          <ArrowLeft size={14}/> Back to Studio
        </Link>
        <div style={{ marginTop: 12 }}>
          <span className="studio-eyebrow"><Settings size={12}/> Studio settings</span>
          <h2 style={{ margin: "6px 0 18px" }}>Your notifications &amp; availability</h2>
        </div>

        {/* Notification preferences */}
        <div className="studio-card" data-testid="notif-prefs-card">
          <h3><Bell size={14} style={{ verticalAlign: "-2px" }}/> Notification channels</h3>
          <p className="studio-muted">Choose how Studio reaches you. Off-channels are silenced everywhere.</p>
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {[
              ["whatsapp", "WhatsApp"],
              ["email", "Email"],
              ["in_app", "In-app (bell icon)"],
              ["slack", "Slack (coming soon)"],
            ].map(([k, label]) => (
              <label key={k} className="studio-toggle" data-testid={`notif-${k}`}>
                <input
                  type="checkbox"
                  checked={!!pref[k]}
                  onChange={(e) => savePref({ ...pref, [k]: e.target.checked })}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Availability */}
        <div className="studio-card" style={{ marginTop: 18 }} data-testid="availability-card">
          <h3><Calendar size={14} style={{ verticalAlign: "-2px" }}/> Working availability</h3>
          <p className="studio-muted">Studio won&apos;t suggest assigning you tasks during off-days or leave.</p>

          <h4 style={{ marginTop: 14, color: "#0F172A" }}>Weekly off</h4>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            {DOW.map((d, i) => (
              <button
                key={i}
                className="studio-chip"
                data-active={avail.weekly_off.includes(i)}
                onClick={() => toggleOff(i)}
                data-testid={`off-${i}`}
              >
                {d}
              </button>
            ))}
          </div>

          <h4 style={{ marginTop: 18, color: "#0F172A" }}>Leave dates</h4>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
            <input
              type="date"
              value={newLeave}
              onChange={(e) => setNewLeave(e.target.value)}
              data-testid="leave-date-input"
            />
            <button
              className="studio-btn-ghost"
              onClick={() => {
                if (newLeave && !avail.leave_dates.includes(newLeave)) {
                  setAvail((a) => ({ ...a, leave_dates: [...a.leave_dates, newLeave] }));
                  setNewLeave("");
                }
              }}
              data-testid="leave-add"
            >
              Add
            </button>
          </div>
          {avail.leave_dates.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
              {avail.leave_dates.map((d) => (
                <span key={d} className="studio-pill studio-pill--sm" data-testid={`leave-${d}`}>
                  {d}
                  <button
                    style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", marginLeft: 4 }}
                    onClick={() => setAvail((a) => ({ ...a, leave_dates: a.leave_dates.filter((x) => x !== d) }))}
                  >×</button>
                </span>
              ))}
            </div>
          )}

          <div className="studio-form-actions" style={{ marginTop: 18 }}>
            <button className="studio-btn-primary" onClick={saveAvail} disabled={saving} data-testid="avail-save">
              {saving ? <Loader2 size={14} className="ac-spin"/> : <Save size={14}/>} Save availability
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
